import { useEffect, useMemo } from 'react';
import { useLocation, useParams, useHistory } from 'react-router-dom';
import findIndex from 'lodash/findIndex';
import QueryString from 'qs';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import toUpper from 'lodash/toUpper';
import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';

import { useAuthenticated } from './auth';

import { rootSiteSelector, siteFromRootSiteSelector } from 'selectors/root-site';
import { langSelector } from 'selectors/lang';
import { buildCallDashboardPath, buildChildSitePath, ROOT_PATH } from 'constants/route-paths';
import { DEFAULT_LANGUAGE } from 'constants/common';
import langActions from 'actions/lang-actions';

const replaceUrlParamsWithLanguage = debounce((dispatch, history, currentLang) => {
  if (isEmpty(currentLang)) return;

  const queryParams = QueryString.parse(history.location.search, { ignoreQueryPrefix: true });
  const { _lang, ...restParams } = queryParams;

  history.replace({
    ...history.location,
    search: QueryString.stringify({
      ...restParams,
      lang: currentLang.default ? undefined : currentLang.symbol,
    }),
  });

  dispatch(langActions.updateLanguage(currentLang.id));
}, 0);

// DEPRECATED replace by useSearchObject
export const useSearchQuery = () => {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
};

export const useActiveSection = (mapSections, defaultActiveIndex = 0) => {
  const { hash } = useLocation();

  const index = findIndex(mapSections, ({ href }) => hash.split('?')[0] === href);
  const indexOfActiveSection = index === -1 ? defaultActiveIndex : index;

  useEffect(() => {
    // make sure always on top after changed location hash
    setTimeout(() => window.scrollTo({ top: 0 }));
  }, [hash]);

  return indexOfActiveSection;
};

export const useUserLandingPath = () => {
  const { siteSlug } = useParams();
  const isAuthenticated = useAuthenticated();

  if (!siteSlug) {
    return ROOT_PATH;
  } else {
    return isAuthenticated ? buildCallDashboardPath(siteSlug) : buildChildSitePath(siteSlug);
  }
};

export const useSearchObject = () => {
  const { search } = useLocation();

  return useMemo(() => QueryString.parse(search, { ignoreQueryPrefix: true }), [search]);
};

export const useDetectLanguage = () => {
  const { siteSlug } = useParams();
  const searchObject = useSearchObject();

  const dispatch = useDispatch();
  const history = useHistory();

  const { supportingLanguages = [], defaultLanguageId } = useSelector(
    siteSlug ? siteFromRootSiteSelector(siteSlug) : rootSiteSelector,
  );
  const { selectedLang } = useSelector((state) => langSelector(state));

  const defaultLang = useMemo(
    () => merge(cloneDeep(DEFAULT_LANGUAGE), find(supportingLanguages, ['id', defaultLanguageId])),
    [defaultLanguageId, supportingLanguages],
  );

  const currentLang = useMemo(() => {
    if (selectedLang === defaultLanguageId) return defaultLang;

    return find(supportingLanguages, ['id', selectedLang]) || defaultLang;
  }, [defaultLang, defaultLanguageId, selectedLang, supportingLanguages]);

  useEffect(() => () => dispatch(langActions.updateLanguage()), [dispatch, siteSlug]);

  useEffect(() => {
    if (!isEmpty(supportingLanguages) && searchObject.lang) {
      const languageId =
        find(supportingLanguages, ['symbol', toUpper(searchObject.lang)])?.id ||
        defaultLanguageId ||
        DEFAULT_LANGUAGE.id;

      dispatch(langActions.updateLanguage(languageId));
    }
  }, [defaultLanguageId, dispatch, searchObject.lang, supportingLanguages]);

  useEffect(() => {
    replaceUrlParamsWithLanguage(dispatch, history, currentLang);
  }, [history, currentLang, dispatch, siteSlug]);

  useEffect(() => {
    if (defaultLang && searchObject.lang === defaultLang.symbol) {
      dispatch(langActions.updateLanguage(defaultLang.id));
    }
  }, [defaultLang, defaultLanguageId, dispatch, history, searchObject.lang]);
};
